import { Button } from "primereact/button";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { queryKeys } from "../../api/query";
import { useCreateInstitutionRanking } from "../../api/queries/institution-rankings/useCreateInstitutionRanking";
import { useDeleteInstitutionRanking } from "../../api/queries/institution-rankings/useDeleteInstitutionRanking";
import { useUpdateInstitutionRanking } from "../../api/queries/institution-rankings/useUpdateInstitutionRanking";
import { useInstitutionRanking } from "../../api/queries/institution-rankings/useInstitutionRanking";
import {
  CreateInstitutionRankingPayload,
  InstitutionRankingValue,
  UpdateInstitutionRankingPayload,
  rankValueOptions,
} from "../../api/types/institution-ranking";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useInstitutions } from "../../api/queries/institutions/useInstitutions";
import { Editor, defaultEditorModules } from "../Editor";

export default function InstitutionRankingForm() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createInstituonRank = useCreateInstitutionRanking();
  const deleteInstituonRank = useDeleteInstitutionRanking();
  const updateInstituonRank = useUpdateInstitutionRanking(uuid);
  const { data: institutionRank } = useInstitutionRanking(uuid);

  const { data: institutions } = useInstitutions({ limit: 100, offset: 0 }); // TODO

  const [institution, setInstitution] = useState<string | null>(null);
  const onInstitutionChange = (event: DropdownChangeEvent) =>
    setInstitution(event.value);

  const [rankValue, setRankValue] = useState<InstitutionRankingValue>(2);
  const onRankValueChange = (event: DropdownChangeEvent): void =>
    setRankValue(event.value as InstitutionRankingValue);

  const [note, setNote] = useState<string>("");
  const onNoteChange = (value: string): void => setNote(value);

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    if (!institution) {
      return; // TODO: validations
    }

    if (uuid) {
      const payload: UpdateInstitutionRankingPayload = {
        institution,
        rank_value: rankValue,
        note,
      };

      await updateInstituonRank.mutateAsync(payload);
      queryClient.invalidateQueries(queryKeys.institutionRanking(uuid));
    } else {
      const payload: CreateInstitutionRankingPayload = {
        institution,
        rank_value: rankValue,
        note,
      };

      await createInstituonRank.mutateAsync(payload);
    }

    queryClient.invalidateQueries(queryKeys.institutionRankingList(), {
      refetchInactive: true,
    });
    navigate("/institution-rankings");
  };

  const handleDelete = async () => {
    await deleteInstituonRank.mutateAsync(uuid);

    queryClient.invalidateQueries(queryKeys.institutionRanking(uuid));
    queryClient.invalidateQueries(queryKeys.institutionRankingList(), {
      refetchInactive: true,
    });

    navigate("/institution-rankings");
  };

  useEffect(() => {
    if (institutionRank) {
      setInstitution(institutionRank.data.institution_id);
      setRankValue(institutionRank.data.rank_value);
      setNote(institutionRank.data.note);
    }
  }, [institutionRank]);

  return (
    <form onSubmit={handleSubmit} className="col-12">
      <div className="p-fluid">
        <div className="field">
          <label>Institution:</label>
          <Dropdown
            options={institutions?.data.results || []}
            value={institution}
            optionLabel="name"
            optionValue="id"
            onChange={onInstitutionChange}
          />
        </div>

        <div className="field">
          <label>Rank value:</label>
          <Dropdown
            options={rankValueOptions}
            value={rankValue}
            optionLabel="label"
            optionValue="value"
            onChange={onRankValueChange}
          />
        </div>

        <div className="field">
          <label>Note:</label>
          <Editor
            content={note}
            onChange={onNoteChange}
            modules={defaultEditorModules}
          />
        </div>
      </div>

      <div className="flex gap-1 justify-content-end">
        {uuid && (
          <Button
            type="button"
            label="Remove"
            outlined={true}
            className="p-button-sm p-button-danger"
            onClick={handleDelete}
          />
        )}

        <Button
          type="submit"
          label="Save"
          className="p-button-main p-button-sm"
        />
      </div>
    </form>
  );
}
